// Reklama kampaniyalari — xsib-core `venue` moduli, `CampaignController`.
//
// ⚠️ PUL BU YERDA YO'Q (`AdCampaign.java` javadoc): modul faqat ko'rsatish
// FAKTINI qayd etadi. Hisob-kitob `ledger` da — bu yerda narx maydoni yo'q
// va bo'lmasligi kerak.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { requireEveryField } from '@/shared/api/fullReplace'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import {
  listParams,
  serverPageSchema,
  toPageResult,
  type SortFieldMap,
} from '@/shared/api/serverPage'
import { showsClosed } from '@/shared/ui/table/closedFilter'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const CAMPAIGN_STATES = ['DRAFT', 'ACTIVE', 'PAUSED', 'FINISHED'] as const
export type CampaignState = (typeof CAMPAIGN_STATES)[number]

const campaignSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  venueId: uuidSchema.nullish(),
  name: z.string(),
  advertiserName: z.string(),
  advertiserPartyId: uuidSchema.nullish(),
  creativeRef: z.string().nullish(),
  rewardBytes: z.number().nullish(),
  rewardSeconds: z.number().nullish(),
  minViewSeconds: z.number(),
  dailyCapPerGuest: z.number(),
  startsAt: instantSchema.nullish(),
  endsAt: instantSchema.nullish(),
  state: z.enum(CAMPAIGN_STATES),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface AdCampaign {
  id: string
  /** `null` = tenantning barcha ob'ektlarida ishlaydi */
  venueId: string | null
  name: string
  advertiserName: string
  /**
   * `identity.party` UUID'i — reklama beruvchining kontragent yozuvi.
   *
   * ⚠️ Panelda TAHRIRLANMAYDI, lekin O'QILADI va qaytariladi: `PUT`
   * to'liq almashtiradi va ro'yxat qatorida bu maydon bo'lmasa uni
   * qaytarib bo'lmasdi. Sabab `campaignDraftBody` da.
   */
  advertiserPartyId: string | null
  creativeRef: string | null
  rewardBytes: number | null
  rewardSeconds: number | null
  minViewSeconds: number
  dailyCapPerGuest: number
  startsAt: string | null
  endsAt: string | null
  state: CampaignState
}

function toCampaign(raw: z.infer<typeof campaignSchema>): AdCampaign {
  return {
    id: raw.id,
    venueId: raw.venueId ?? null,
    name: raw.name,
    advertiserName: raw.advertiserName,
    advertiserPartyId: raw.advertiserPartyId ?? null,
    creativeRef: raw.creativeRef ?? null,
    rewardBytes: raw.rewardBytes ?? null,
    rewardSeconds: raw.rewardSeconds ?? null,
    minViewSeconds: raw.minViewSeconds,
    dailyCapPerGuest: raw.dailyCapPerGuest,
    startsAt: raw.startsAt ?? null,
    endsAt: raw.endsAt ?? null,
    state: raw.state,
  }
}

/**
 * ⚠️ HAR MAYDON MAJBURIY — `PUT /api/venues/campaigns/{id}` TO'LIQ
 * ALMASHTIRADI: `JdbcCampaignRepository.update` har ustunga qiymat yozadi
 * (`advertiser_party_id = CAST(:partyId AS uuid)`, `COALESCE` yo'q).
 * Ixtiyoriy (`?`) maydon shu tozalashni tip tekshiruvidan o'tkazardi.
 */
export interface AdCampaignDraft {
  /** `null` = tenantning barcha ob'ektlarida */
  venueId: string | null
  name: string
  advertiserName: string
  advertiserPartyId: string | null
  creativeRef: string | null
  rewardBytes: number | null
  rewardSeconds: number | null
  /** `null` = server sukut qiymati (5 soniya) */
  minViewSeconds: number | null
  /** `null` = server sukut qiymati (1) */
  dailyCapPerGuest: number | null
  startsAt: string | null
  endsAt: string | null
}

/** Serverdagi `AdCampaignDraft.java` record'i bilan solishtiriladigan ro'yxat. */
export const CAMPAIGN_DRAFT_FIELDS: readonly (keyof AdCampaignDraft)[] = [
  'venueId',
  'name',
  'advertiserName',
  'advertiserPartyId',
  'creativeRef',
  'rewardBytes',
  'rewardSeconds',
  'minViewSeconds',
  'dailyCapPerGuest',
  'startsAt',
  'endsAt',
]

/** `CampaignFormModal` beradigan shakl — sanalar allaqachon ISO satrga aylangan. */
export interface CampaignFormValues {
  venueId?: string | null
  name: string
  advertiserName: string
  creativeRef?: string | null
  /** Operator GB'da o'ylaydi, baza baytda saqlaydi */
  rewardGb?: number | null
  rewardSeconds?: number | null
  minViewSeconds?: number | null
  dailyCapPerGuest?: number | null
  startsAt: string
  endsAt: string
}

const BYTES_IN_GB = 1024 ** 3

/**
 * So'rov tanasi — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `advertiserPartyId` FORMADA YO'Q va bu qaror: bu `identity.party`
 * ga havola, FK'siz (modul chegarasi — ADR-016) va panelda kontragent
 * tanlagichi umuman yo'q. Tanlagich qurish boshqa modulning ro'yxatini
 * shu formaga tortish degani bo'lardi. Lekin ustunda haqiqiy bog'lanish
 * turishi mumkin (u tashqaridan qo'yiladi), va `UPDATE` uni shartsiz
 * yozadi — shuning uchun joriy qiymat QAYTARILADI.
 */
export function campaignDraftBody(
  values: CampaignFormValues,
  campaign: AdCampaign | null,
): AdCampaignDraft {
  return {
    venueId: values.venueId ?? null,
    name: values.name.trim(),
    advertiserName: values.advertiserName.trim(),
    advertiserPartyId: campaign?.advertiserPartyId ?? null,
    creativeRef: values.creativeRef?.trim() || null,
    rewardBytes:
      values.rewardGb === null || values.rewardGb === undefined
        ? null
        : Math.round(values.rewardGb * BYTES_IN_GB),
    rewardSeconds: values.rewardSeconds ?? null,
    minViewSeconds: values.minViewSeconds ?? null,
    dailyCapPerGuest: values.dailyCapPerGuest ?? null,
    startsAt: values.startsAt,
    endsAt: values.endsAt,
  }
}

/**
 * Ustun kaliti → `GET /api/venues/campaigns` ning `sortField` enumi.
 *
 * ⚠️ `period` ustuni IKKI sanani ko'rsatadi (boshlanish — tugash), server
 * esa bittasi bo'yicha saralaydi. Shuning uchun u `STARTS_AT` ga
 * bog'landi: «qaysi kampaniya keyingi ishga tushadi» degan savol
 * amaliyotda so'raladigan savol.
 */
const CAMPAIGN_SORT_FIELDS: SortFieldMap = {
  name: 'NAME',
  advertiser: 'ADVERTISER_NAME',
  state: 'STATE',
  period: 'STARTS_AT',
  endsAt: 'ENDS_AT',
  createdAt: 'CREATED_AT',
}

/** Holat filtri jadval holatida shu kalit ostida yashaydi (ustun kaliti ham shu) */
export const CAMPAIGN_STATE_FILTER = 'state'

/**
 * Kampaniya uchun «yopiq» — FAQAT `FINISHED` (`CampaignState.isClosed()`).
 *
 * ⚠️ `PAUSED` va `DRAFT` ro'yxatda QOLADI: birinchisi qaytarib
 * yoqiladi, ikkinchisi hali tayyorlanmoqda. Ularni yashirish yarim
 * tayyor kampaniyani ko'zdan yo'qotardi va u hech qachon ishga
 * tushmasdi.
 */
export const CAMPAIGN_CLOSED_STATE: CampaignState = 'FINISHED'

function isCampaignState(value: string | undefined): value is CampaignState {
  return value !== undefined && (CAMPAIGN_STATES as readonly string[]).includes(value)
}

/**
 * So'rov parametrlari — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ ESKI IZOH BEKOR QILINDI. Bu yerda «serverdagi yagona saqlangan
 * filtr — `venueId`, holat bo'yicha filtr YO'Q» deb yozilgan edi va
 * xulosa to'g'ri edi: mijozdagi filtr sahifalangan jadvalda yolg'on
 * javob berardi. 2026-08-21 da `CampaignResource#list` `state` va
 * `includeClosed` ni oldi — ya'ni to'siq yo'qoldi, filtr esa SERVERGA
 * boradi.
 *
 * ⚠️ `includeClosed` DOIM yuboriladi: serverdagi sukut qiymat `true`.
 * `state` aniq berilganda server uni e'tiborsiz qoldiradi
 * (`CampaignQuery.excludesClosed`).
 */
export function campaignListParams(
  state: TableState,
  venueId: string | null,
): Record<string, unknown> {
  const chosen = state.filters[CAMPAIGN_STATE_FILTER]?.[0]
  return {
    ...listParams(state, { sortFields: CAMPAIGN_SORT_FIELDS }),
    ...(venueId ? { venueId } : {}),
    ...(isCampaignState(chosen) ? { state: chosen } : {}),
    includeClosed: showsClosed(chosen),
  }
}

/** Bir SAHIFA kampaniya — qidiruv, saralash, filtr va sahifalash SERVERDA */
export function fetchCampaignPage(venueId: string | null) {
  return async (state: TableState, signal: AbortSignal): Promise<PageResult<AdCampaign>> => {
    const response = await api.get<unknown>('/api/venues/campaigns', {
      signal,
      params: campaignListParams(state, venueId),
    })
    const parsed = serverPageSchema(campaignSchema).parse(response.data)
    return toPageResult(parsed, parsed.rows.map(toCampaign))
  }
}

/**
 * Sukut ko'rinish NECHTA kampaniyani yashirganini sanaydi.
 *
 * ⚠️ Holat AYNAN `FINISHED` ga qo'yiladi — server shu holatda bayroqni
 * e'tiborsiz qoldiradi va qatorlarni ROSTDAN qaytaradi. `size: 1` —
 * javobdan faqat `total` kerak.
 */
export function campaignClosedCountParams(
  state: TableState,
  venueId: string | null,
): Record<string, unknown> {
  return {
    ...campaignListParams(
      { ...state, filters: { ...state.filters, [CAMPAIGN_STATE_FILTER]: [CAMPAIGN_CLOSED_STATE] } },
      venueId,
    ),
    page: 0,
    size: 1,
  }
}

export function countClosedCampaigns(venueId: string | null) {
  return async (state: TableState, signal: AbortSignal): Promise<number> => {
    const response = await api.get<unknown>('/api/venues/campaigns', {
      signal,
      params: campaignClosedCountParams(state, venueId),
    })
    return serverPageSchema(campaignSchema).parse(response.data).total
  }
}

export async function fetchCampaign(id: string): Promise<AdCampaign> {
  const response = await api.get<unknown>(`/api/venues/campaigns/${id}`)
  return toCampaign(campaignSchema.parse(response.data))
}

export async function createCampaign(draft: AdCampaignDraft): Promise<AdCampaign> {
  requireEveryField(draft, CAMPAIGN_DRAFT_FIELDS, 'kampaniya')
  const response = await api.post<unknown>('/api/venues/campaigns', draft)
  return toCampaign(campaignSchema.parse(response.data))
}

export async function updateCampaign(id: string, draft: AdCampaignDraft): Promise<AdCampaign> {
  requireEveryField(draft, CAMPAIGN_DRAFT_FIELDS, 'kampaniya')
  const response = await api.put<unknown>(`/api/venues/campaigns/${id}`, draft)
  return toCampaign(campaignSchema.parse(response.data))
}

/**
 * Kampaniyani BUTUNLAY o'chiradi — `DELETE /api/venues/campaigns/{id}`.
 *
 * <h2>⚠️ QATTIQ O'CHIRISH, "yopish" EMAS — o'lchangan</h2>
 * `JdbcCampaignRepository.delete` — haqiqiy `DELETE FROM venue.ad_campaign`,
 * holat bayrog'i emas. Bu loyihadagi YAGONA shunday amal va u ataylab
 * shunday: `venue`/`site` da o'chirish yo'q, chunki ularga bog'langan
 * tarix har doim bo'ladi.
 *
 * ⚠️ SHART SERVERDA VA U TAXMIN EMAS, O'LCHOV (`CampaignServiceImpl`):
 *   · holat `DRAFT` bo'lishi shart — aks holda **409**;
 *   · ko'rsatuvlar soni **0** bo'lishi shart (bazadan sanaladi) — aks
 *     holda ham **409**.
 * Ya'ni o'chirish faqat "yo'qoladigan tarix umuman yo'q" holatda
 * mumkin. Panel birinchi shartni BILADI (`state`), ikkinchisini
 * BILMAYDI — shuning uchun rad javobi ekranda ko'rsatilishi shart.
 *
 * ⚠️ `ad_impression.campaign_id` da `ON DELETE CASCADE` turibdi, ya'ni
 * shart buzilsa statistika JIMGINA o'chib ketardi. Tekshiruv serverda
 * turgani uchun panel uni takrorlamaydi.
 *
 * ⚠️ Javob TANASIZ (`XsibResponse<Void>`) — zod bilan o'qiladigan qator
 * yo'q, shuning uchun bu yerda `parse` ham yo'q.
 */
export async function deleteCampaign(id: string): Promise<void> {
  await api.delete<unknown>(`/api/venues/campaigns/${id}`)
}

/**
 * Holatni o'zgartirish — QUERY PARAMETR, JSON tana emas.
 *
 * ⚠️ `FINISHED` dan qaytish yo'q (`CampaignService.setState` javadoc) —
 * backend rad etadi, UI bu holatdan tugmalarni chizmaydi.
 */
export async function setCampaignState(id: string, state: CampaignState): Promise<AdCampaign> {
  const response = await api.post<unknown>(`/api/venues/campaigns/${id}/state`, undefined, {
    params: { state },
  })
  return toCampaign(campaignSchema.parse(response.data))
}

const statsSchema = z.object({
  campaignId: uuidSchema,
  from: instantSchema,
  to: instantSchema,
  started: z.number(),
  completed: z.number(),
  uniqueGuests: z.number(),
})

export interface ImpressionStats {
  started: number
  /** Faqat shular mukofotga aylandi */
  completed: number
  uniqueGuests: number
  /** Backend hisoblamaydi — shu yerda */
  completionPercent: number
}

/** ⚠️ Reklama beruvchiga faqat SHU ko'rsatiladi — mehmon ma'lumoti hech qachon */
export async function fetchCampaignStats(id: string, from: string, to: string): Promise<ImpressionStats> {
  const response = await api.get<unknown>(`/api/venues/campaigns/${id}/stats`, {
    params: { from, to },
  })
  const parsed = statsSchema.parse(response.data)
  return {
    started: parsed.started,
    completed: parsed.completed,
    uniqueGuests: parsed.uniqueGuests,
    completionPercent: parsed.started === 0 ? 0 : (parsed.completed * 100) / parsed.started,
  }
}
